import { Alert, AlertDescription, AlertTitle, Skeleton } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { PageHeader } from '@/design';
import type { IAddedSurveyRouteState } from '@/domains/auth';
import type { IMyCycleResponse } from '../api/contracts';
import { receiptPath, responseFormPath } from '../paths';
import {
  AddSurveyCard,
  AddedNotice,
  EmptyCatalogueCard,
  ResponseRow,
  emptyDashboard,
  namesParticipants,
  responsesOf,
  useCanAdd,
  useMyCatalogue,
  type ICycleResponse
} from './catalogue';
import { IdentifierConfirmations } from './identifier-confirmation-card';
import { StartSurveyCard, isStartable } from './start-survey-card';

/**
 * My surveys — every survey cycle `GET /my/cycles` lists for this account, each annotated with
 * the account's own responses in it: this cycle's, the start affordance, and past cycles below.
 *
 * Reading **cycles** rather than responses is what lets one screen answer both questions a
 * respondent has — what have I filled, and what can I fill. The narrower `GET /my/responses`
 * lists live responses only, so a cycle nobody has started is invisible in it, and so is anything
 * `archived` (a `recycled` response is in neither list); it also carries neither the
 * `surveyCycleId` a draft is opened against nor the path codes a multi-path survey requires.
 * Whom each survey is for — and, for a team, where survey-api routes it — is the cycle's own
 * `eligibleParticipants`, so the start affordance knows before it is clicked whether it has a
 * choice to offer, and what. The participant list
 * (`GET /my/participants`) is read beside the cycles for the names and the start menu's photos alone
 * (`catalogue.tsx`, which Home reads too).
 *
 * Where the account acts for more than one participant — an advisor, their team, their firm — or
 * for one it reaches as a roster's member or an advisor's assistant, each half of the list is
 * grouped by participant, so "whose is this" is a heading rather than a phrase in every row: the
 * account's own in the order the catalogue first names them, then those headed "Listed on <name>"
 * and "for <name>" (SR-487 ruling 12, the owner's Q15).
 *
 * Starting a survey goes through `POST /api/v1/responses`, survey-api's own route since SR-213
 * retired the dev endpoint this page used to call.
 *
 * It is also where survey-api's one question about a number on record is asked
 * (`GET /my/identifiers`, SR-213 block 3) — as a card among the rest, not a screen in front of
 * them, because survey-api ruled that question no gate. `identifier-confirmation-card.tsx` says
 * what it asks and what it must not claim. Home asks the same, in the same card.
 *
 * And it is where an advisor **adds a survey** (`ADD_SURVEY_ROUTE`, the teams onboarding plan's
 * R7): one more number registered inside this account — the advisor's own, a team's lead's, a
 * firm's — entitles its participant to that survey, which is the only way this list grows. The
 * card is drawn for the `advisor` role alone, as Home draws it; the add lands back here naming
 * what it added, in router state.
 */
export function MySurveysPage() {
  const navigate = useNavigate();
  const { cycles, participants, identifiers, error, setError, answered, awaiting } =
    useMyCatalogue();
  const canAdd = useCanAdd();
  const location = useLocation();
  // What `ADD_SURVEY_ROUTE` added, said once, on arrival: kept here and taken out of the history
  // entry at once, or a reload — and a Back onto this entry — would announce it again.
  const [added] = React.useState(
    () => (location.state as Nullable<IAddedSurveyRouteState>)?.added
  );
  React.useEffect(() => {
    if (!added) return;
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: null }
    );
    // Once, on arrival: the entry is replaced by the same address with no state.
  }, []);

  // Straight to the receipt for a submitted response. The form would redirect
  // there anyway, but only after loading a state document and a definition
  // snapshot to reach the conclusion this list already holds. Every other status
  // opens the form, which goes read-only on its own when the response is locked.
  const open = (response: IMyCycleResponse) =>
    navigate(
      response.status === 'submitted'
        ? receiptPath(response.responseId)
        : responseFormPath(response.responseId)
    );

  const loaded = cycles ?? [];
  const grouped = namesParticipants(participants);
  const active = loaded.filter((cycle) => cycle.phase === 'active');
  const startable = active.filter(isStartable);
  const empty = emptyDashboard(loaded, startable);
  const mine = responsesOf(loaded, participants);
  const current = mine.filter((row) => row.cycle.phase === 'active');
  const past = mine.filter((row) => row.cycle.phase === 'historical');
  // An empty list is two states that look alike — nothing is offered to this account, and the
  // read that would have said so failed — and the card below only knows how to say the first.
  // So it is withheld when the list is empty *and* something went wrong, and kept when a start
  // failed over a list that did arrive.
  const emptyShown = mine.length === 0 && (error === null || loaded.length > 0);
  // Where nothing at all is offered, the way to add a survey is said in that card itself.
  const addInEmpty = canAdd && emptyShown && loaded.length === 0;

  return (
    <>
      <PageHeader
        title="My surveys"
        lede="Every survey asked of you, your team and your firm — this cycle's and the ones before."
      />

      <div className="mx-auto grid w-full max-w-[1120px] gap-6 px-6 pt-6 pb-16 lg:px-10">
        {error && (
          <Alert variant="destructive">
            <AlertTitle>Something went wrong</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {added && <AddedNotice added={added} />}

        {cycles === null ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <>
            <IdentifierConfirmations
              identifiers={identifiers}
              participants={participants}
              onAnswered={answered}
            />

            {emptyShown && <EmptyCatalogueCard empty={empty} withAdd={addInEmpty} />}

            {current.length > 0 && (
              <section aria-labelledby="my-surveys-current" className="grid gap-3">
                <h2 id="my-surveys-current" className={LIST_HEADING}>
                  This cycle
                </h2>
                <ResponseGroups rows={current} grouped={grouped} onOpen={open} />
              </section>
            )}

            <StartSurveyCard
              cycles={startable}
              waiting={awaiting}
              canAdd={canAdd}
              participants={participants}
              onOpened={(responseId) => navigate(responseFormPath(responseId))}
              onError={setError}
            />

            {canAdd && !addInEmpty && <AddSurveyCard />}

            {past.length > 0 && (
              <section aria-labelledby="my-surveys-past" className="mt-4 grid gap-3">
                <h2 id="my-surveys-past" className={LIST_HEADING}>
                  Past cycles
                </h2>
                <ResponseGroups rows={past} grouped={grouped} onOpen={open} />
              </section>
            )}
          </>
        )}
      </div>
    </>
  );
}

const LIST_HEADING = 'text-[1.375rem] font-normal text-[var(--color-royal-blue)]';

/**
 * The rows, as they came — or, grouped, under one heading per participant: the account's own in
 * the order the catalogue first names each, then the member's and the assistant's (Q15). The
 * heading then says whose they are, so the rows under it do not repeat it.
 */
function ResponseGroups({
  rows,
  grouped,
  onOpen
}: {
  rows: ICycleResponse[];
  grouped: boolean;
  onOpen: (response: IMyCycleResponse) => void;
}) {
  if (!grouped) {
    return rows.map((row) => <ResponseRow key={row.response.responseId} row={row} onOpen={onOpen} />);
  }

  const groups = new Map<string, { name: string; relayed: boolean; rows: ICycleResponse[] }>();
  for (const row of rows) {
    const { participantId } = row.response;
    const group = groups.get(participantId) ?? {
      name: row.participant,
      relayed: row.relayed,
      rows: []
    };
    group.rows.push({ ...row, showParticipant: false });
    groups.set(participantId, group);
  }
  // A stable sort: the account's own keep their order, and so do the rest after them.
  const ordered = [...groups].sort(([, a], [, b]) => Number(a.relayed) - Number(b.relayed));

  return ordered.map(([participantId, group]) => (
    <ParticipantGroup key={participantId} name={group.name}>
      {group.rows.map((row) => (
        <ResponseRow key={row.response.responseId} row={row} onOpen={onOpen} />
      ))}
    </ParticipantGroup>
  ));
}

/** One participant's rows under their name — a group the heading names, so the rows belong to
 *  that name for a screen reader as they do on screen, and a spec can find a row by whose it is. */
function ParticipantGroup({ name, children }: { name: string; children: React.ReactNode }) {
  const headingId = React.useId();
  return (
    <div role="group" aria-labelledby={headingId} className="grid gap-2.5">
      <h3
        id={headingId}
        className="mt-2 text-[0.9375rem] font-medium tracking-normal text-muted-foreground"
      >
        {name}
      </h3>
      {children}
    </div>
  );
}
